import { DISCOUNT_MODES, DISCOUNT_TYPES, LOYALTY_PROGRAM_TYPE } from "../data/constants/shared";
import { REDEMPTION_TYPES, REWARD_EXPIRY } from "./constants";
import type { ProgramDetailsEnvelope } from "./types";

/**
 * Milestone REWARDS — the second thing a loyalty programme can hand back.
 *
 * 🔴 **A programme redeems one way or the other, never both.** `redemptionType` decides it:
 * `inr` means points come off the bill as money (the redemption panel), `rista_deal` means the
 * customer unlocks named rewards at point thresholds and each one is applied as a DEAL. Showing
 * both surfaces at once offers the same points twice, which is why `canRedeemPointsOnPos` now
 * asks `isPointsRedemption` and this file gates the reward rows on `isRewardRedemption`.
 *
 * Pure — arguments in, answer out, no store and no react. The views (`@rista/ui`'s
 * `ItemLoyaltyRewardSection` and the customer summary's Loyalty tab) hold none of this.
 */

/* ── which redemption a programme runs ───────────────────────────────────────────────── */

/** Points are spent as MONEY off the bill — the redemption panel's mode. */
export const isPointsRedemption = (programDetails?: ProgramDetailsEnvelope | null): boolean =>
  programDetails?.data?.redemptionType === REDEMPTION_TYPES.INR;

/**
 * Points unlock milestone REWARDS, applied as deals — the reward rows' mode.
 *
 * ⚠️ Read off the CUSTOMER's `loyaltyInfo`, not off the programme, because that is where the
 * milestones themselves arrive; the programme envelope answers the same question for the panel.
 * Both spellings are the provider's, and neither is derived from the other.
 */
export const isRewardRedemption = (customerLoyaltyInfo?: any): boolean =>
  customerLoyaltyInfo?.redemptionType === REDEMPTION_TYPES.RISTA_DEAL;

/**
 * Is this a points programme — the only kind the POS can redeem against, either way?
 *
 * Here rather than in `./provider` (which asks it for `canRedeemPointsOnPos`) so that
 * `redeemsAsRewards` below can ask it too without the two files importing each other.
 */
export const isPointProgram = (programDetails?: ProgramDetailsEnvelope | null): boolean =>
  programDetails?.data?.programType === LOYALTY_PROGRAM_TYPE.POINT;

/**
 * Does the customer's standing read as REWARDS rather than a points balance? The cart's customer
 * chip asks, and says "2 rewards available" instead of "240 Points available" when it does.
 *
 * ⚠️ **Not `isRewardRedemption`, and the difference is deliberate** (DM-146, 2026-09-21). This
 * reads the PROGRAMME's `redemptionType` first and falls back to the customer's, and only for a
 * POINT programme; `isRewardRedemption` reads the customer's alone, because that is where the
 * milestones the reward rows pair arrive. The chip is describing the programme's currency, so the
 * programme answers first.
 */
export const redeemsAsRewards = (
  programDetails?: ProgramDetailsEnvelope | null,
  customerLoyaltyInfo?: any,
): boolean =>
  isPointProgram(programDetails) &&
  (programDetails?.data?.redemptionType ?? customerLoyaltyInfo?.redemptionType) ===
    REDEMPTION_TYPES.RISTA_DEAL;

/* ── identifying a reward ────────────────────────────────────────────────────────────── */

/**
 * A milestone reward's stable id — its `code`.
 *
 * ⚠️ This is the id that matches a DEAL's `discountId`, which is why pairing uses it and not the
 * milestone's own key. Kept as one function because three surfaces join on it.
 */
export const getRewardId = (reward?: any): string | undefined => reward?.code;

/**
 * Every id a deal might be known by, as strings.
 *
 * 🔴 **The offers API and `billDiscountList` do not agree on a single key**, which is the whole
 * reason this is a list: the same deal arrives as `mid` from the catalogue, `discountId` from the
 * offers leg, and `couponCode`/`discountCode` once it is on the bill. Matching on any one of them
 * alone silently drops rows for one of those sources.
 */
const dealIdsOf = (deal: any): string[] =>
  [deal?.id, deal?.discountId, deal?.mid, deal?.code, deal?.discountCode, deal?.couponCode]
    .filter(Boolean)
    .map(String);

/**
 * The deals currently on offer, narrowed to the ones the deals sheet is actually listing.
 *
 * `uniqueDeals` is whatever the caller holds — the sheet keeps a map, other callers an array —
 * and `discountIds` is the sheet's own visible order. Filtering by it keeps a stale or
 * out-of-channel deal from surfacing as a reward.
 */
export function dealsForDiscountIds(uniqueDeals: any, discountIds: (string | number)[] = []): any[] {
  const available = new Set((discountIds || []).map(String));
  const deals = Array.isArray(uniqueDeals) ? uniqueDeals : Object.values(uniqueDeals || {});
  return deals.filter((deal: any) => dealIdsOf(deal).some((id) => available.has(id)));
}

/** The `discountCode`s of everything applied to this bill — the join key for "is it redeemed?". */
export function appliedDiscountCodes(billDiscountList: any[] = []): Set<string> {
  return new Set((billDiscountList || []).map((entry) => entry?.discountCode).filter(Boolean));
}

/* ── redeeming a reward that names an item ───────────────────────────────────────────── */

// Web v1's `DiscountDeals.redeemLoyaltyReward` and `unredeemLoyaltyReward`, as rules. The
// discount v2 module runs them (revamps/loyalty.md); the item add and the apply stay its writes.

/** The catalogue id of the item a reward was redeemed for: `mid`, else `id`, as web reads it. */
export const rewardItemId = (item: any): string => String(item?.mid || item?.id || "");

/** A sale line names its item by `itemId`, an older line by `mid`. */
const lineItemId = (line: any): string => String(line?.itemId || line?.mid);

/** Does the sale already hold a line of this item? Then redeeming adds nothing, it only re-ties. */
export const hasRewardItem = (sale: any, itemId: string): boolean =>
  (sale?.saleItems ?? []).some((line: any) => lineItemId(line) === itemId);

/**
 * Is this reward's deal already on the bill? The row's own ground truth: the deal's
 * `discountCode` among the applied ones (`appliedDiscountCodes`). A deal with no code is never
 * "already redeemed".
 */
export const isRewardRedeemed = (sale: any, deal: any): boolean =>
  !!deal?.discountCode && appliedDiscountCodes(sale?.billDiscountList).has(deal.discountCode);

/**
 * The sale's lines with the reward's item marked `isSelectedForDiscount` and every other line
 * unmarked. Only one item may carry it at a time (`d307964f4`): the backend's pricing applies an
 * `applyOnlyOnSelectedItems` deal to marked lines alone. Copies the lines; never mutates.
 */
export function markRewardItem(saleItems: any[] = [], itemId: string): any[] {
  return saleItems.map((line) => ({ ...line, isSelectedForDiscount: lineItemId(line) === itemId }));
}

/**
 * The bill entry removing a reward strips. Removal matches on `discountId`, and a deal from the
 * catalogue often carries only `mid`, so the id is normalised from it, as web did.
 */
export const rewardRemovalEntry = (deal: any): any => ({ ...deal, discountId: deal?.discountId || deal?.mid });

/* ── pairing milestones with the deals that fulfil them ──────────────────────────────── */

/**
 * May this deal be offered as a milestone reward at all?
 *
 * 🔴 **Sharing an id with a milestone's reward code is not enough.** Only a SIMPLE discount —
 * `Percentage` or `Absolute`, applied `Manual`ly or by `Coupon` — that the sale/customer/offers
 * response actually returned (`offerDiscountIds`) qualifies. A catalogue discount that merely
 * carries the same id is not the reward the customer earned (DM-146).
 */
export function isRewardOfferEligible(deal: any, offerDiscountIds: Set<string> = new Set()): boolean {
  return (
    offerDiscountIds.has(String(deal?.discountId)) &&
    (deal?.discountType === DISCOUNT_TYPES.percentage || deal?.discountType === DISCOUNT_TYPES.absolute) &&
    (deal?.mode === DISCOUNT_MODES.manual || deal?.mode === DISCOUNT_MODES.coupon)
  );
}

/**
 * One milestone as a row — the shape both surfaces draw. `deal` is always set on the deals
 * sheet's rows (`pairRewardsWithDeals` drops a milestone without one) and may be `null` on the
 * customer summary's (`groupMilestonesByStatus` describes every milestone it can place).
 */
export interface RewardRow {
  milestone: any;
  deal: any;
  eligibleItems: any[];
}

export interface PairRewardsInput {
  milestones?: any[];
  deals?: any[];
  /** From `appliedDiscountCodes(saleData.billDiscountList)`. */
  appliedCodes?: Set<string>;
  /**
   * The `discountId`s the sale/customer/offers response returned. ⚠️ Omitting it means NO deal
   * qualifies — see `isRewardOfferEligible`.
   */
  offerDiscountIds?: Set<string>;
  /** Resolves the menu items a deal may be redeemed against. Omit for a caller with no catalogue. */
  getEligibleItems?: (deal: any) => any[];
}

/**
 * One row per milestone that has a matching deal AND still belongs on screen.
 *
 * 🔴 **A just-redeemed milestone must keep its row.** The customer refetch that follows a
 * successful redeem returns it `eligible: false, consumed: true` — so a "still redeemable" test
 * alone makes the row vanish the instant it is used, taking its Remove control with it and
 * stranding the deal on the bill. Hence the second arm: already applied to THIS bill counts.
 *
 * A milestone with no matching deal is dropped rather than rendered inert: without a deal there
 * is nothing to apply, and a Redeem button that cannot do anything is worse than no row. So is one
 * whose deal fails `isRewardOfferEligible` — and that check runs BEFORE the on-the-bill arm, so it
 * drops an already-applied reward's row too.
 */
export function pairRewardsWithDeals({
  milestones = [],
  deals = [],
  appliedCodes = new Set<string>(),
  offerDiscountIds = new Set<string>(),
  getEligibleItems,
}: PairRewardsInput): RewardRow[] {
  return (milestones || [])
    .map((milestone: any) => {
      const rewardId = getRewardId(milestone?.reward);
      if (!rewardId) return null;
      const deal = deals.find((item: any) => String(item?.discountId) === String(rewardId));
      if (!deal) return null;
      if (!isRewardOfferEligible(deal, offerDiscountIds)) return null;

      const isRedeemable = !!milestone?.eligible && !milestone?.consumed;
      const isAlreadyOnBill = !!deal?.discountCode && appliedCodes.has(deal.discountCode);
      if (!isRedeemable && !isAlreadyOnBill) return null;

      return {
        milestone,
        deal,
        eligibleItems: getEligibleItems ? getEligibleItems(deal) : [],
      };
    })
    .filter(Boolean) as RewardRow[];
}

/**
 * Should the reward section be drawn at all?
 *
 * Three conditions, and each one alone is insufficient: the programme has to work this way, the
 * customer has to be IN it (an unenrolled customer's milestones are a preview, not an
 * entitlement), and there has to be at least one row worth showing.
 */
export function shouldShowRewardSection(customerLoyaltyInfo: any, rows: RewardRow[]): boolean {
  return isRewardRedemption(customerLoyaltyInfo) && !!customerLoyaltyInfo?.isEnrolled && rows.length > 0;
}

/* ── the customer summary's read-only grouping ───────────────────────────────────────── */

/**
 * The deal a milestone's reward resolves to, looked up in each source IN ORDER — the first source
 * that lists it wins. `null` when none does.
 *
 * Matched across every id spelling (`dealIdsOf`), for the reason given there. The order is the
 * caller's statement of which copy to trust: the customer summary passes the partner's offers
 * before the catalogue — see `groupMilestonesByStatus`.
 */
export function findRewardDeal(rewardId: string | undefined, sources: any[][] = []): any | null {
  if (!rewardId) return null;
  const id = String(rewardId);
  for (const deals of sources) {
    const deal = (deals || []).find((candidate: any) => dealIdsOf(candidate).includes(id));
    if (deal) return deal;
  }
  return null;
}

/**
 * Can the deal behind a reward no longer be taken — paused by the merchant, or past its end date?
 *
 * The two reasons `getRewardValidity` keeps apart (and the row banners apart), joined: the
 * grouping only needs to know that the reward is off the table, not whose doing it was. A reward
 * with no deal at all is NOT unredeemable by this test — nothing says it is paused or expired.
 */
export function isRewardUnredeemable(deal: any, now: Date = new Date()): boolean {
  const { isUnavailable, isExpired } = getRewardValidity(deal, now);
  return isUnavailable || isExpired;
}

export interface GroupedMilestones {
  /** Earned, not yet spent, and still on offer. */
  unclaimed: RewardRow[];
  /** Not yet earned — `pointsToUnlock` says how far off — and still on offer. */
  upcoming: RewardRow[];
  /** Spent, OR no longer on offer: its deal is paused or expired (`isRewardUnredeemable`). */
  redeemed: RewardRow[];
}

/**
 * What the Loyalty tab draws: the groups, plus the balance `pointsToUnlock` measures an upcoming
 * reward against. `useCustomerRewards` returns it.
 */
export interface CustomerRewards extends GroupedMilestones {
  currentPoints: number;
}

export interface GroupMilestonesOptions {
  /**
   * Where to look for each reward's deal, in order (`findRewardDeal`). Omitted, every row's `deal`
   * is `null` and nothing can be unredeemable.
   */
  dealSources?: any[][];
  /** Resolves the menu items a deal may be redeemed against. Omit for a caller with no catalogue. */
  getEligibleItems?: (deal: any) => any[];
  now?: Date;
}

/**
 * The customer summary's Loyalty tab groups the SAME milestones by where the customer stands,
 * with no bill in play. Each comes back as the row the deals sheet also draws — the milestone,
 * its deal (or `null`), and the items that deal may be redeemed against.
 *
 * 🔴 **A reward whose deal is paused or expired is "redeemed and unavailable", whatever the
 * milestone's own `eligible` flag says** (DM-146, 2026-09-21). That flag is about the customer's
 * POINTS; it says nothing about the deal, which the merchant can pause and whose window can close
 * while the milestone still reads `eligible: true` — so read alone, it listed a reward the
 * customer cannot take under Unclaimed. Only the DEAL knows, and only the partner's copy of it:
 * the programme link's `status` and the reward's validity live on the offer, and the catalogue's
 * copy of the same discount carries neither. So the summary passes the offers first and the
 * catalogue as a fallback for a reward the offers response does not list.
 *
 * ⚠️ `upcoming` deliberately requires the customer to be SHORT of the threshold, not merely
 * ineligible. A milestone the customer can afford but is barred from for another reason (a
 * cool-off, a channel restriction) is not "earn N more points to unlock" — promising that would
 * be a lie the tab cannot take back.
 *
 * A milestone that is neither consumed, nor eligible, nor short of its threshold, and whose deal
 * is still on offer, appears in no group. That is intended: there is nothing true to say about it.
 */
export function groupMilestonesByStatus(
  milestones: any[] = [],
  currentPoints: number = 0,
  { dealSources = [], getEligibleItems, now = new Date() }: GroupMilestonesOptions = {},
): GroupedMilestones {
  const list = Array.isArray(milestones) ? milestones : [];
  const rows = list.map((milestone: any) => {
    const deal = findRewardDeal(getRewardId(milestone?.reward), dealSources);
    return {
      row: { milestone, deal, eligibleItems: deal && getEligibleItems ? getEligibleItems(deal) : [] },
      isUnredeemable: isRewardUnredeemable(deal, now),
    };
  });
  const rowsWhere = (test: (milestone: any, isUnredeemable: boolean) => boolean): RewardRow[] =>
    rows.filter(({ row, isUnredeemable }) => test(row.milestone, isUnredeemable)).map(({ row }) => row);

  return {
    unclaimed: rowsWhere((m, isUnredeemable) => !!m?.eligible && !m?.consumed && !isUnredeemable),
    upcoming: rowsWhere(
      (m, isUnredeemable) =>
        !m?.consumed && !m?.eligible && !isUnredeemable && currentPoints < (m?.thresholdPoints || 0),
    ),
    redeemed: rowsWhere((m, isUnredeemable) => !!m?.consumed || isUnredeemable),
  };
}

/** How many more points this milestone needs. Never negative. */
export const pointsToUnlock = (milestone: any, currentPoints: number = 0): number =>
  Math.max((milestone?.thresholdPoints || 0) - currentPoints, 0);

/* ── one reward row's own state ──────────────────────────────────────────────────────── */

/** Whole days from now until `dateStr` (end of that day). `null` when there is no usable date. */
export function daysUntil(dateStr?: string, now: Date = new Date()): number | null {
  if (!dateStr) return null;
  const end = new Date(dateStr);
  if (Number.isNaN(end.getTime())) return null;
  end.setHours(23, 59, 59, 999);
  return Math.ceil((end.getTime() - now.getTime()) / REWARD_EXPIRY.MS_PER_DAY);
}

export interface RewardValidity {
  /** The merchant unlinked or paused the deal — still listed, no longer redeemable. */
  isUnavailable: boolean;
  isExpired: boolean;
  isExpiringSoon: boolean;
  /** `null` when the deal carries no end date. */
  daysLeft: number | null;
}

/**
 * Why a reward cannot be taken, if it cannot.
 *
 * ⚠️ `isUnavailable` and `isExpired` are separate answers with separate banners: one is the
 * merchant's doing and may come back, the other is the calendar's and will not. `status` is only
 * consulted when the deal HAS one — a deal with no status field is not thereby paused.
 */
export function getRewardValidity(deal: any, now: Date = new Date()): RewardValidity {
  const isUnavailable = !!deal && !!deal.status && deal.status !== REWARD_EXPIRY.ACTIVE_STATUS;
  const daysLeft = daysUntil(deal?.endDate, now);
  const isExpired = daysLeft !== null && daysLeft < 0;
  const isExpiringSoon =
    !isExpired && daysLeft !== null && daysLeft <= REWARD_EXPIRY.SOON_THRESHOLD_DAYS;
  return { isUnavailable, isExpired, isExpiringSoon, daysLeft };
}

/**
 * What the customer saved, for the redeemed banner — or `null` rather than a guess.
 *
 * 🔴 **The applied bill entry is the ground truth**: its `amount` is negative and is what was
 * actually taken off. The item price and the deal's configured value are fallbacks for the
 * moment before the recalculated sale arrives, and they can disagree with the final figure.
 * Everything failing means the banner says nothing — a fabricated number on a receipt-adjacent
 * surface is worse than a missing one.
 */
export function getRewardSavedAmount(item: any, deal: any, billEntry: any): number | null {
  const billAmount = Math.abs(Number(billEntry?.amount));
  if (Number.isFinite(billAmount) && billAmount > 0) return billAmount;
  const raw =
    item?.price ??
    item?.sellingPrice ??
    item?.info?.price ??
    item?.pricingInfo?.sellingPrice ??
    deal?.discountAmount ??
    deal?.amount ??
    deal?.value;
  const amount = Number(raw);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

/** The row's heading, from whichever of the milestone's or the deal's names is present. */
export function getRewardTitle(milestone: any, deal: any): string {
  const details = milestone?.reward || {};
  return (
    milestone?.rewardLabel ||
    details?.title ||
    deal?.title ||
    details?.name ||
    deal?.discountCode ||
    "Reward"
  );
}
